// MarkPaidControl — the "Mark paid" button and its proof form, shared by the dashboard row, the student
// detail row and the Payments page. Marking paid always records proof of the FULL amount: a reference for
// bank/M-Pesa/any other type, or payer + receiver + amount for cash. The server validates all of it
// (active type, exact cash amount, unused reference); this form only collects it.
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useMarkPaymentPaid } from "../../hooks/useMarkPaymentPaid.js";
import { useProofTypes } from "../../hooks/useProofTypes.js";
import { formatAmount, normaliseProofReference } from "@dm-lms/shared/utils";
import { formatKes } from "../../lib/formatMoney.js";
import { PROOF_PARTY_LENGTH, PROOF_REFERENCE_LENGTH, PROOF_TYPE_CASH } from "@dm-lms/shared/constants";
import type { Payment, PaymentWithProof } from "@dm-lms/shared/types";

interface MarkPaidControlProps {
  payment:      Pick<Payment, "id" | "code" | "amountKes">;
  onPaid:       (payment: PaymentWithProof) => void;
  buttonLabel?: string;
  buttonClass?: string;
}

const DEFAULT_BUTTON_CLASS =
  "rounded-md border border-green-600 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50";
const FIELD_CLASS =
  "w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function MarkPaidControl({ payment, onPaid, buttonLabel = "Mark paid", buttonClass = DEFAULT_BUTTON_CLASS }: MarkPaidControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  if (!isOpen) {
    return (
      <button type="button" onClick={() => setIsOpen(true)} className={buttonClass}>
        {buttonLabel}
      </button>
    );
  }
  return <ProofForm payment={payment} onPaid={onPaid} onCancel={() => setIsOpen(false)} />;
}

function ProofForm({ payment, onPaid, onCancel }: Pick<MarkPaidControlProps, "payment" | "onPaid"> & { onCancel: () => void }) {
  const { user } = useAuth();
  const { options, isLoading, error: loadError } = useProofTypes(true);
  const { isSubmitting, error, markPaid } = useMarkPaymentPaid(payment, onPaid);

  const [chosenType, setChosenType] = useState("");
  const [reference, setReference] = useState("");
  const [payer, setPayer] = useState("");
  const [receiver, setReceiver] = useState(user?.name ?? "");   // the confirming admin, editable
  const [amount, setAmount] = useState("");

  const proofType = chosenType || options[0]?.key || "";
  const isCash = proofType === PROOF_TYPE_CASH;
  const partyOk = (value: string) => value.trim().length >= PROOF_PARTY_LENGTH.min;
  const canSubmit =
    proofType !== "" &&
    !isSubmitting &&
    amount !== "" &&
    (isCash ? partyOk(payer) && partyOk(receiver) : reference.trim().length >= PROOF_REFERENCE_LENGTH.min);

  // The amount received is required for every proof type; the server checks it equals the payment's.
  async function submit() {
    const amountKes = Number(amount);
    await markPaid(
      isCash ? { proofType: PROOF_TYPE_CASH, payer, receiver, amountKes } : { proofType, reference, amountKes }
    );
  }

  return (
    <form
      className="w-full max-w-xs space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-left"
      onSubmit={(event) => { event.preventDefault(); void submit(); }}
    >
      <p className="text-xs text-dm-slate">
        Record proof that {payment.code} ({formatKes(payment.amountKes)}) was paid in full. This is logged and visible to admins only.
      </p>

      {isLoading && <p className="text-xs text-dm-slate">Loading proof types…</p>}
      {loadError && <p className="text-xs text-red-600">{loadError}</p>}

      {options.length > 0 && (
        <>
          <label className="block text-xs font-medium text-dm-navy">
            Proof type
            <select value={proofType} onChange={(event) => setChosenType(event.target.value)} className={`mt-1 ${FIELD_CLASS}`}>
              {options.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
          </label>

          {isCash ? (
            <>
              <label className="block text-xs font-medium text-dm-navy">
                Paid by
                <input type="text" value={payer} maxLength={PROOF_PARTY_LENGTH.max} onChange={(event) => setPayer(event.target.value)} className={`mt-1 ${FIELD_CLASS}`} />
              </label>
              <label className="block text-xs font-medium text-dm-navy">
                Received by
                <input type="text" value={receiver} maxLength={PROOF_PARTY_LENGTH.max} onChange={(event) => setReceiver(event.target.value)} className={`mt-1 ${FIELD_CLASS}`} />
              </label>
            </>
          ) : (
            <label className="block text-xs font-medium text-dm-navy">
              Reference
              <input type="text" value={reference} maxLength={PROOF_REFERENCE_LENGTH.max} onChange={(event) => setReference(normaliseProofReference(event.target.value))} className={`mt-1 font-mono ${FIELD_CLASS}`} />
            </label>
          )}

          <label className="block text-xs font-medium text-dm-navy">
            Amount received (KES) — must equal {formatAmount(payment.amountKes)}
            <input type="number" min={1} step={1} value={amount} onChange={(event) => setAmount(event.target.value)} className={`mt-1 ${FIELD_CLASS}`} />
          </label>
        </>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-dm-navy px-3 py-1.5 text-xs font-medium text-white transition hover:bg-dm-blue disabled:opacity-50"
        >
          {isSubmitting ? "Saving…" : "Confirm payment"}
        </button>
        <button type="button" onClick={onCancel} disabled={isSubmitting} className="rounded-md px-3 py-1.5 text-xs font-medium text-dm-slate hover:text-dm-navy">
          Cancel
        </button>
      </div>
    </form>
  );
}
